import React, { useState, useRef, useEffect } from 'react';
import { FaStar } from 'react-icons/fa';
import { MdTrendingUp, MdAttachMoney, MdMoreVert } from 'react-icons/md';
import { AiOutlineDelete } from 'react-icons/ai';

const ProductCard = ({ product, onClick, onRemove, showActions = true, createdAt }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRemove = (e) => {
    e.stopPropagation();
    setShowDropdown(false);
    if (onRemove) {
      onRemove(product.asin);
    }
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  return (
    <div
      onClick={onClick}
      className="bg-secondary-bg border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-all duration-300 cursor-pointer hover:shadow-lg hover:shadow-primary/10 group"
    >
      {/* Product Image */}
      <div className="relative bg-white p-4 h-48 flex items-center justify-center overflow-hidden">
        <img
          src={product.image}
          alt={product.basicInfo?.productName}
          className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-2 right-2 bg-primary/90 text-white px-2 py-1 rounded-md text-xs font-semibold">
          #{product.overview?.BSR?.BSR || 'N/A'}
        </div>
      </div>

      {/* Product Details */}
      <div className="p-4">
        {/* Brand & Category with Actions */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-primary text-xs font-semibold">
            {product.basicInfo?.brand || 'N/A'}
          </span>
          <span className="text-gray-600 text-xs">•</span>
          <span className="text-gray-500 text-xs truncate">
            {product.basicInfo?.category?.split(',')[0] || 'N/A'}
          </span>
          </div>

          {/* Three Dots Action Button */}
          {showActions && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={toggleDropdown}
                className="p-1 hover:bg-primary-bg rounded-full transition-colors duration-200"
              >
                <MdMoreVert className="text-gray-400 hover:text-white text-lg" />
              </button>

              {/* Dropdown Menu */}
              {showDropdown && (
                <div className="absolute right-0 top-8 z-20 bg-secondary-bg border border-border rounded-lg shadow-lg overflow-hidden min-w-[160px]">
                  <button
                    onClick={handleRemove}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-primary-bg transition-colors duration-200"
                  >
                    <AiOutlineDelete className="text-base" />
                    Remove
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Product Name */}
        <h3 className="text-white font-semibold text-sm mb-3 line-clamp-2 min-h-[2.5rem]">
          {product.basicInfo?.productName || 'Product Name Unavailable'}
        </h3>

        {/* Rating */}
        {product.rating && (
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-1">
              <FaStar className="text-yellow-500 text-sm" />
              <span className="text-white font-semibold text-sm">
                {product.rating.rating}
              </span>
            </div>
            <span className="text-gray-500 text-xs">
              ({product.rating.ratings_total?.toLocaleString()} reviews)
            </span>
          </div>
        )}

        {/* BSR & Max Cost */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-primary-bg rounded-lg p-2">
            <div className="flex items-center gap-1 mb-1">
              <MdTrendingUp className="text-primary text-sm" />
              <span className="text-gray-400 text-xs">BSR</span>
            </div>
            <div className="flex items-center gap-1">
              {product.overview?.BSR?.BSR && product.overview.BSR.BSR > 0 ? (
                <>
                  <p className="text-white font-bold text-sm">
                    #{product.overview.BSR.BSR}
                  </p>
                  {product.overview.BSR.percentage && (
                    <span className="text-primary text-xs font-semibold">
                      ({product.overview.BSR.percentage})
                    </span>
                  )}
                </>
              ) : (
                <p className="text-white font-bold text-sm">-</p>
              )}
            </div>
          </div>
          <div className="bg-primary-bg rounded-lg p-2">
            <div className="flex items-center gap-1 mb-1">
              <MdAttachMoney className="text-green-500 text-sm" />
              <span className="text-gray-400 text-xs">Max Cost</span>
            </div>
            <p className="text-green-500 font-bold text-sm">
              {product.overview?.max_cost && product.overview.max_cost > 0 
                ? `$${product.overview.max_cost}` 
                : '-'}
            </p>
          </div>
        </div>

        {/* ASIN & At */}
        <div className="pt-3 border-t border-border space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-gray-400 text-xs">ASIN:</span>
            <span className="text-white font-semibold text-sm font-mono">
              {product.asin || 'N/A'}
            </span>
          </div>
          {createdAt && (
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-xs"></span>
              <span className="text-gray-300 text-xs">
                {new Date(createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;

